<template>
  <vs-wrapper>
    <v-skeleton-loader
      v-if="props.isLoading && props.availablePriceBookItems === undefined"
      data-test="line-item-select-loading"
      type="image"
      max-height="40"
      rounded
    />
    <vs-text-input
      v-else-if="
        !props.isLoading &&
        props.availablePriceBookItems !== undefined &&
        props.availablePriceBookItems.length === 0
      "
      :value="selected"
      label="Product/service"
      data-test="line-item-select-textfield"
      required
      @input="updateLineItem"
    />
    <vs-autocomplete
      v-else
      :value="selected"
      :items="transformedPriceBookOptions"
      data-test="line-item-select"
      label="Product/service"
      free-form-create
      required
      @input="updateLineItem"
    />
  </vs-wrapper>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import VsAutocomplete from '@/components/vision/Inputs/VsAutocomplete/VsAutocomplete.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import { useStore } from 'vuex-composition-helpers'
import { useCurrencyFormatter } from '@/utils/number-formatting'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'

interface Props {
  value: string
  assignedToServiceProviderId?: string | null
  hubId: string
  isLoading: boolean
  availablePriceBookItems: any // should be PriceBookItem[] | undefined once we have vue 3.3
}

const props = withDefaults(defineProps<Props>(), {
  assignedToServiceProviderId: null,
})

const emit = defineEmits<{
  (
    e: 'select-price-book-item',
    value: {
      id: string | null
      name: string
      defaultRate: number | null
    },
  )
}>()

const selected = computed(() => props.value)

const store = useStore()
const hasLineItemRateAccessMethod = computed<
  (serviceProviderId: string | null, hubId: string) => boolean
>(() => store.getters['permissions/hasLineItemRateAccess'])
const hasLineItemRateAccess = computed(() =>
  hasLineItemRateAccessMethod.value(props.assignedToServiceProviderId, props.hubId),
)

const formatter = useCurrencyFormatter(5)

const transformedPriceBookOptions = computed(() => {
  const options: {
    text: string
    value: { id: string; defaultRate: number | null; name: string }
    description: string
    actionText: string | null
  }[] = []

  if (props.availablePriceBookItems) {
    props.availablePriceBookItems.forEach((item) => {
      options.push({
        text: item.name,
        description: formatDescription(item.description),
        actionText:
          hasLineItemRateAccess.value && item.defaultRate !== null
            ? formatter.format(item.defaultRate)
            : null,
        value: {
          id: item.id,
          name: item.name,
          defaultRate: item.defaultRate,
        },
      })
    })
  }

  return options
})

function formatDescription(description: string | null) {
  // truncate descriptions that are too long to display in a dropdown
  if (description && description.length > 125) {
    return description.slice(0, 125) + '...'
  }
  return description ?? ''
}

function updateLineItem(
  value: string | { id: string; name: string; defaultRate: number | null } | null,
) {
  if (!value) return

  if (typeof value === 'string') {
    // we don't want to re-emit if the name hasn't changed
    if (selected.value === value) return
    emit('select-price-book-item', {
      id: null,
      defaultRate: null,
      name: value,
    })
  } else {
    emit('select-price-book-item', {
      id: value.id,
      defaultRate: value.defaultRate,
      name: value.name,
    })
  }
}
</script>

<style scoped></style>
